import React from 'react';
import { Card, Table, Row, Col, Tabs } from 'antd';
import { useOwnerCustomers } from '@/hooks/useOwnerIntelligence';
import { useCurrency } from '@/contexts/CurrencyContext';
import { SignalBadge, PrimaryKpiCard, OiPageFrame } from '@/components/owner-intelligence/OwnerIntelligenceComponents';
import { OiDonutChart } from '@/components/owner-intelligence/charts/OiCharts';
import type { RagStatus } from '@/services/ownerIntelligence.service';

const CustomerIntelligencePage: React.FC = () => {
  const { data, isLoading, error } = useOwnerCustomers();
  const { formatCurrency } = useCurrency();

  const healthColumns = [
    { title: 'Customer', dataIndex: 'customer_name' },
    {
      title: 'Health',
      dataIndex: 'health_score',
      render: (v: number, row: { health_status: RagStatus }) => (
        <span>
          {v}% <SignalBadge tier={row.health_status === 'green' ? 'healthy' : row.health_status === 'amber' ? 'watch' : 'at_risk'} />
        </span>
      ),
    },
    { title: 'Revenue YTD', dataIndex: 'revenue_ytd', render: (v: number) => formatCurrency(v) },
    { title: 'Share %', dataIndex: 'revenue_share_pct', render: (v: number) => `${v.toFixed(1)}%` },
    { title: 'Outstanding', dataIndex: 'outstanding', render: (v: number) => formatCurrency(v) },
    { title: 'Overdue', dataIndex: 'overdue', render: (v: number) => formatCurrency(v) },
  ];

  const donutData = (data?.customer_health ?? []).slice(0, 5).map((c) => ({
    name: c.customer_name,
    value: c.revenue_ytd,
  }));

  return (
    <OiPageFrame
      subtitle="Customer health scores, payment behavior, and revenue concentration."
      loading={isLoading && !data}
      error={Boolean(error)}
      errorMessage="Customer intelligence unavailable"
    >
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={8}>
          <PrimaryKpiCard label="Customers tracked" value={(data?.customer_health ?? []).length} />
        </Col>
        <Col xs={12} sm={8}>
          <PrimaryKpiCard label="Pipeline value" value={formatCurrency(Number(data?.pipeline_value ?? 0))} />
        </Col>
        <Col xs={12} sm={8}>
          <PrimaryKpiCard label="High-risk accounts" value={(data?.categories?.high_risk ?? []).length} />
        </Col>
      </Row>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} md={8}>
          <Card title="Revenue concentration" bordered={false}>
            <OiDonutChart data={donutData} />
          </Card>
        </Col>
        <Col xs={24} md={16}>
          <Card title="Customer health scorecard" bordered={false}>
            <Table size="small" rowKey="customer_id" dataSource={data?.customer_health ?? []} columns={healthColumns} />
          </Card>
        </Col>
      </Row>

      <Tabs
        items={[
          {
            key: 'best',
            label: 'Best customers',
            children: (
              <Table size="small" rowKey="customer_id" dataSource={data?.categories?.best_customers ?? []} columns={healthColumns} />
            ),
          },
          {
            key: 'slow',
            label: 'Slow payers',
            children: (
              <Table size="small" rowKey="customer_id" dataSource={data?.categories?.slow_paying ?? []} columns={healthColumns} />
            ),
          },
          {
            key: 'risk',
            label: 'High risk',
            children: (
              <Table size="small" rowKey="customer_id" dataSource={data?.categories?.high_risk ?? []} columns={healthColumns} />
            ),
          },
          {
            key: 'top-revenue',
            label: 'Top revenue',
            children: (
              <Table size="small" rowKey="customer_id" dataSource={data?.categories?.growing ?? []} columns={healthColumns} />
            ),
          },
        ]}
      />
    </OiPageFrame>
  );
};

export default CustomerIntelligencePage;
